import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import {
    BankTransactionReimbursement,
    ContributionTotalsEntry,
    ContributionType,
    FetchState,
    FetchStateKey,
    HsaState,
    Transaction,
    TransactionApiStatus,
    TransactionFilter,
    TransactionResponse,
    TransactionSource,
    TransactionStatus,
    TransactionTotalsResponse,
    ContributionActivityList,
    ContributionActivity
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as bankService from "@/shared/api/bankService";
import * as expenseService from "@/shared/api/expenseService";
import {
    transactionGetFormattedDisplayDescription,
    transactionGetFormattedDisplayStatus
} from "@/shared/transactionHelper";
import { templatedFetchKeyCollectionStore } from "@/shared/store/fetchKeyedCollectionPlugin";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import { defineStore } from "pinia";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useUserStore } from "./user";

const emptyState: HsaState = {
    fetchState: FetchState.Uninitialized,
    fetchKey: null,
    transactionTotalFetchState: FetchState.Uninitialized,
    transactionsDictionary: {},
    reimbursementTransactionExpenses: [],
    increasingTransactionsDictionary: {},
    decreasingTransactionsDictionary: {},
    contributionsDictionary: {},
    contributionTotalsDictionary: {},
    page: 0,
    transactionPageCount: 0,
    increasingTransactionPageCount: 0,
    decreasingTransactionPageCount: 0,
    contributionsPageCount: 0,
    contributionsFilter: ContributionType.All
};

const mapRecords = (item: Transaction) => {

    const custodianSettingsStore = useCustodianSettingsStore();

    const transactionPayrollOneTimeIsAdjustment =
        custodianSettingsStore.userCustodianSettings.transactionPayrollOneTimeIsAdjustment;
    const updatedTransactionObject = {
        ...item,
        displaySource: item.displaySource,
        displayDescription: transactionGetFormattedDisplayDescription(item)
    };
    let transactionStatus;
    const moneyIntoAccount = item.amount > 0;
    switch (item.source) {
        case TransactionSource.Reimbursement:
            transactionStatus = TransactionStatus.Reimbursed;
            break;
        case TransactionSource.DebitCard:
            transactionStatus = TransactionStatus.HSADebitCard;
            break;
        case TransactionSource.PendingActivity:
            transactionStatus = TransactionStatus.Processing;
            break;
        case TransactionSource.Investment:
            switch (item.status) {
                case TransactionApiStatus.Complete:
                    transactionStatus = TransactionStatus.Complete;
                    break;
                case TransactionApiStatus.Processing:
                    transactionStatus = TransactionStatus.Processing;
                    break;
                case TransactionApiStatus.Rejected:
                    transactionStatus = TransactionStatus.Rejected;
                    break;
                default:
                    transactionStatus = TransactionStatus.UnKnown;
                    break;
            }
            break;
        case TransactionSource.PayrollOneTime:
            if (transactionPayrollOneTimeIsAdjustment) {
                transactionStatus = TransactionStatus.Adjustment;
                break;
            }
            transactionStatus = TransactionStatus.Contributed;
            break;
        case TransactionSource.Contribution:
        case TransactionSource.PayrollEmployer:
        case TransactionSource.PayrollRecurring:
        case TransactionSource.PersonalContribution:
        case TransactionSource.PennyDeposit:
        case TransactionSource.PayrollPostTax:
            transactionStatus = TransactionStatus.Contributed;
            break;
        case TransactionSource.Fee:
            transactionStatus = TransactionStatus.Fee;
            break;
        case TransactionSource.Disbursement:
            transactionStatus = TransactionStatus.Disbursed;
            break;
        default:
            transactionStatus = TransactionStatus.UnKnown;
            break;
    }

    updatedTransactionObject.moneyIncreasing = moneyIntoAccount;
    updatedTransactionObject.displayStatus = transactionGetFormattedDisplayStatus(transactionStatus);

    return updatedTransactionObject;
};

const useAllContributionActivitySubStore = defineStore("hsa_allContributionActivity", () => {
    return templatedFetchKeyCollectionStore<ContributionActivityList, ContributionActivityList>(
        bankService.getAllContributionActivity,
    );
});

const useAllContributionActivityAdminSubStore = defineStore("hsa_allContributionActivity_admin", () => {
    return templatedFetchKeyCollectionStore<ContributionActivityList, ContributionActivityList>(
        bankService.getAllContributionActivityAdmin,
    );
});

function getContributionActivityKey (page: number, contributionType?: ContributionType): string {
    let itemKey = `${page}`;
    if (contributionType) {
        itemKey += `-${contributionType}`;
    }

    return itemKey;
}

export const useHsaStore = defineStore("hsa", {
    state: (): HsaState => (_.cloneDeep(emptyState)),
    getters: {
        contributionTotalsLoaded () {
            return (year: number | any) => {
                const yearData = this.contributionTotalsDictionary[year];
                return !!yearData && yearData.fetchState === FetchState.Loaded;
            };
        },
        transactionsForCurrentPage () {
            return (_.has(this.transactionsDictionary, this.page) ?
                this.transactionsDictionary[this.page] : []);
        },
        contributionsLoaded () {
            return this.fetchState === FetchState.Loaded;
        },
        increasingTransactionsForCurrentPage: state => (_.has(state.increasingTransactionsDictionary, state.page) ?
            state.increasingTransactionsDictionary[state.page] : []),
        decreasingTransactionsForCurrentPage: state => (_.has(state.decreasingTransactionsDictionary, state.page) ?
            state.decreasingTransactionsDictionary[state.page] : []),
        contributionsForCurrentPage () {
            return (_.has(this.contributionsDictionary, this.page) ?
                this.contributionsDictionary[this.page] : []);
        },
        pageNumber: state => state.page,
        totalsLoaded: state => state.transactionTotalFetchState === FetchState.Loaded,
        totalAmountReimbursed () {
            return (year?: number) => {
                const dictionaryLookup = this.contributionTotalsDictionary[year || moment().year()];

                if (dictionaryLookup) {
                    return dictionaryLookup.totalAmountReimbursed;
                }
                return 0;
            };
        },
        totalEmployerPayrollContributions () {
            return (year: number) => {
                const dictionaryLookup = this.contributionTotalsDictionary[year || moment().year()];

                if (dictionaryLookup) {
                    return dictionaryLookup.totalEmployerPayrollContributions;
                }

                return 0;
            };
        },
        employeePreTaxContributions () {
            return (year: number) => {
                const dictionaryLookup = this.contributionTotalsDictionary[year || moment().year()];

                if (dictionaryLookup) {
                    return dictionaryLookup.employeePreTaxContributions;
                }

                return 0;
            };
        },
        employeePostTaxContributions () {
            return (year?: number) => {
                const dictionaryLookup = this.contributionTotalsDictionary[year || moment().year()];

                if (dictionaryLookup) {
                    return dictionaryLookup.employeePostTaxContributions;
                }

                return 0;
            };
        },
        totalEmployeePersonalContributions () {
            return (year?: number) => {
                const dictionaryLookup = this.contributionTotalsDictionary[year || moment().year()];

                if (dictionaryLookup) {
                    return dictionaryLookup.pendingEmployeePersonalContributions +
                        dictionaryLookup.actualEmployeePersonalContributions;
                }

                return 0;
            };
        },
        pendingEmployeePersonalContributions () {
            return (year?: number) => {
                const dictionaryLookup = this.contributionTotalsDictionary[year || moment().year()];

                if (dictionaryLookup) {
                    return dictionaryLookup.pendingEmployeePersonalContributions;
                }

                return 0;
            };
        },
        actualEmployeePersonalContributions () {
            return (year: number) => {
                const dictionaryLookup = this.contributionTotalsDictionary[year || moment().year()];

                if (dictionaryLookup) {
                    return dictionaryLookup.actualEmployeePersonalContributions;
                }

                return 0;
            };
        },
        bankDirectContributions () {
            return (year?: number) => {
                const dictionaryLookup = this.contributionTotalsDictionary[year || moment().year()];

                if (dictionaryLookup) {
                    return dictionaryLookup.bankDirectContributions;
                }

                return 0;
            };
        },
        reimbursementExpenses () {
            return this.reimbursementTransactionExpenses;
        },
        allContributionActivity () {
            const userStore = useUserStore();
            return userStore.isAdminPortal
                ? useAllContributionActivityAdminSubStore()
                : useAllContributionActivitySubStore();
            },
        isAllContributionActivityPageLoaded () {
            return (page: number, contributionType?: ContributionType) => {
                const key = getContributionActivityKey(page, contributionType);

                return this.allContributionActivity.fetchStates[key] &&
                    this.allContributionActivity.fetchStates[key] === FetchState.Loaded;
            };
        },
        allContributionActivityForPage (): (page: number, contributionType?: ContributionType) => ContributionActivity[] {
            return (page: number, contributionType?: ContributionType) => {
                if (!this.isAllContributionActivityPageLoaded(page, contributionType)) {
                    return [];
                }

                const key = getContributionActivityKey(page, contributionType);
                return this.allContributionActivity.items[key].contributionActivity;
            };
        },
        allContributionActivityPageCount () {
            return (page: number, contributionType?: ContributionType) => {
                if (!this.isAllContributionActivityPageLoaded(page, contributionType)) {
                    return 0;
                }

                const key = getContributionActivityKey(page, contributionType);
                return this.allContributionActivity.items[key].numberOfPages;
            };
        }
    },
    actions: {
        setHsaStateLoading (key: FetchStateKey): void {
            this.fetchState = FetchState.Loading;
            this.fetchKey = key;
        },
        invalidateFetchState (key: FetchStateKey): void {
            if (fetchStateKeyMatch(key, this.fetchKey)) {
                return;
            }
            const stateClone = _.cloneDeep(emptyState);
            for (const key in stateClone) {
                this[key] = stateClone[key];
            }
        },
        setTransactionTotalStateLoading (args: { year: number, key: FetchStateKey }): void {
            const entry: ContributionTotalsEntry = {
                fetchState: FetchState.Loading,
                totalAmountReimbursed: 0,
                totalEmployerPayrollContributions: 0,
                employeePreTaxContributions: 0,
                employeePostTaxContributions: 0,
                pendingEmployeePersonalContributions: 0,
                actualEmployeePersonalContributions: 0,
                bankDirectContributions: 0
            };
            Vue.set(this.contributionTotalsDictionary, args.year, entry);
            this.fetchKey = args.key;
        },
        setHsaPageNumber (page: number): void {
            this.page = page;
        },
        setHsaTransactionsDictionary (data: TransactionResponse): void {
            const recordsMapped = data.records.map(mapRecords);

            Vue.set(this.transactionsDictionary, `${data.page}`, recordsMapped);
            this.transactionPageCount = data.numberOfPages;
            this.page = data.page;
        },
        setHsaIncreasingTransactionsDictionary (data: TransactionResponse): void {
            const recordsMapped = data.records.map(mapRecords);

            Vue.set(this.increasingTransactionsDictionary, `${data.page}`, recordsMapped);
            this.increasingTransactionPageCount = data.numberOfPages;
            this.page = data.page;
        },
        setHsaDecreasingTransactionsDictionary (data: TransactionResponse): void {
            const recordsMapped = data.records.map(mapRecords);

            Vue.set(this.decreasingTransactionsDictionary, `${data.page}`, recordsMapped);
            this.decreasingTransactionPageCount = data.numberOfPages;
            this.page = data.page;
        },
        setHsaContributionsDictionary (data: TransactionResponse, filter: ContributionType): void {
            const recordsMapped = data.records.map(mapRecords);

            Vue.set(this.contributionsDictionary, `${data.page}`, recordsMapped);
            this.contributionsPageCount = data.numberOfPages;
            this.page = data.page;
            this.contributionsFilter = filter;
        },
        setHsaStateLoaded (): void {
            this.fetchState = FetchState.Loaded;
        },
        setTransactionTotals (data: TransactionTotalsResponse): void {
            const entry: ContributionTotalsEntry = {
                fetchState: FetchState.Loaded,
                totalAmountReimbursed: data.totalSpent,
                totalEmployerPayrollContributions: data.totalEmployerPayrollContributions,
                employeePreTaxContributions: data.employeePreTaxContributions || 0,
                employeePostTaxContributions: data.employeePostTaxContributions || 0,
                pendingEmployeePersonalContributions: data.pendingEmployeePersonalContributions || 0,
                actualEmployeePersonalContributions: data.actualEmployeePersonalContributions || 0,
                bankDirectContributions: data.bankDirectContributions || 0
            };
            Vue.set(this.contributionTotalsDictionary, data.year, entry);
        },
        setReimbursementExpenses (data: BankTransactionReimbursement[]): void {
            this.reimbursementTransactionExpenses = data;
        },
        loadHsaTransactionsDictionary (params: {
            pageNumber: number,
            transactionFilter: TransactionFilter,
            includeExpenses: boolean,
            username: string,
            partnerId: number,
            ofType?: TransactionSource,
            includePending?: boolean,
            pageSize?: number
        }): Promise<any> {

            const fetchStateKey: FetchStateKey = {
                username: params.username,
                partnerId: params.partnerId
            };

            this.invalidateFetchState(fetchStateKey);

            // already loading ANYTHING then ditch out
            if (this.fetchState === FetchState.Loading) return Promise.resolve();

            let activeTransactionsDictionary = this.transactionsDictionary;

            if (params.transactionFilter === TransactionFilter.Increasing) {
                activeTransactionsDictionary = this.increasingTransactionsDictionary;
            } else if (params.transactionFilter === TransactionFilter.Decreasing) {
                activeTransactionsDictionary = this.decreasingTransactionsDictionary;
            }

            // assume page in dictionary is a loaded page
            if (activeTransactionsDictionary &&
                activeTransactionsDictionary[params.pageNumber]) return Promise.resolve();

            this.setHsaStateLoading(fetchStateKey);

            return bankService.getSavedTransactions(
                params.pageNumber,
                params.transactionFilter,
                params.username,
                params.partnerId,
                params.ofType,
                params.includePending,
                params.pageSize,
            ).then(response => {
                    if (!fetchStateKeyMatch(this.fetchKey, fetchStateKey)) {
                        return;
                    }
                    if (params.transactionFilter === TransactionFilter.Increasing) {
                        this.setHsaIncreasingTransactionsDictionary(response.data);
                    } else if (params.transactionFilter === TransactionFilter.Decreasing) {
                        this.setHsaDecreasingTransactionsDictionary(response.data);
                    } else {
                        this.setHsaTransactionsDictionary(response.data);
                    }

                    const bankTransactions: string[] = [];

                    if (params.includeExpenses) {
                        response.data.records.forEach(transaction => {
                            if (transaction.source === TransactionSource.Reimbursement ||
                                transaction.source === TransactionSource.DebitCard ||
                                transaction.source === TransactionSource.Disbursement) {
                                bankTransactions.push(transaction.id.toString());
                            }
                        });
                    }

                    if (bankTransactions.length > 0) {
                        return expenseService.reimbursedExpensesByBankTransactionIds(bankTransactions)
                            .then(response => {
                                this.setReimbursementExpenses(response.data.reimbursements);
                                this.setHsaStateLoaded();
                            })
                            .catch(err =>
                                storeHelper.handleError({ error: err, rethrow: true })
                            )
                            .finally(() => this.setHsaStateLoaded());
                    } else {
                        this.setHsaStateLoaded();
                    }
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadHsaContributionsDictionary (params: {
            pageNumber: number,
            username?: string
            partnerId?: number,
            contributionType?: ContributionType
        }): Promise<any> {
            const fetchStateKey: FetchStateKey = {
                username: params.username,
                partnerId: params.partnerId
            };

            let contributionType = ContributionType.All;
            if (params.contributionType !== null) {
                contributionType = params.contributionType;
            }

            this.invalidateFetchState(fetchStateKey);

            // already loading ANYTHING then ditch out
            if (this.fetchState === FetchState.Loading) return Promise.resolve();

            const activeContributionsDictionary = this.contributionsDictionary;

            // assume page in dictionary is a loaded page
            if (this.contributionsFilter === contributionType
                && activeContributionsDictionary
                && activeContributionsDictionary[params.pageNumber]) return Promise.resolve();

            this.setHsaStateLoading(fetchStateKey);

            return bankService.getSavedContributions(params.pageNumber, params.username, params.partnerId,
                contributionType)
                .then(response => {
                    if (!fetchStateKeyMatch(fetchStateKey, this.fetchKey)) {
                        return;
                    }
                    this.setHsaContributionsDictionary(response.data, contributionType);
                    this.setHsaStateLoaded();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadTransactionTotals (args?: {
            year?: number,
            username?: string,
            partnerId?: number
        }): Promise<any> {

            args = args || {};

            const yearWithDefault = args.year || moment().year();

            const fetchStateKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            this.invalidateFetchState(fetchStateKey);

            const yearTotals = this.contributionTotalsDictionary[yearWithDefault];

            if (yearTotals && yearTotals.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setTransactionTotalStateLoading({ year: yearWithDefault, key: fetchStateKey });

            return bankService.getTransactionTotals(args.username, args.partnerId, yearWithDefault)
                .then(response => {
                    const data = { ...response.data, year: yearWithDefault };
                    if (!fetchStateKeyMatch(fetchStateKey, this.fetchKey)) {
                        return;
                    }
                    this.setTransactionTotals(data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        reload (year: number): Promise<any> {
            const reloadYear = year || moment().year();
            const key = this.fetchKey || { username: "", partnerId: 0 };
            return bankService.getTransactionTotals(key.username, key.partnerId, reloadYear)
                .then(response => {
                    response.data.year = reloadYear;
                    this.setTransactionTotals(response.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        setAllContributionActivityFetchState ({ fetchState, itemKey }) {
            Vue.set(this.allContributionActivity.fetchStates, itemKey, fetchState);
        },
        async loadAllContributionActivity (
            args: {
                username?: string,
                partnerId?: number,
                page: number,
                contributionType?: ContributionType
            }
        ): Promise<any> {
            const itemKey = getContributionActivityKey(args.page, args.contributionType);

            if (this.allContributionActivity.fetchStates[itemKey] === FetchState.Loading ||
                this.allContributionActivity.fetchStates[itemKey] === FetchState.Loaded) {
                return Promise.resolve();
            }

            return fetchContributionActivity(args, itemKey)
                .catch(err => {
                    this.setAllContributionActivityFetchState({
                        itemKey, fetchState: FetchState.Uninitialized
                    });
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        }
    }
});

function fetchContributionActivity (args: {
                username?: string,
                partnerId?: number,
                page: number,
                contributionType?: ContributionType
            }, itemKey) {
    const userStore = useUserStore();
    return userStore.isAdminPortal
        ? useAllContributionActivityAdminSubStore().fetchKeyedApiCall(
                        { username: args.username, partnerId: args.partnerId },
                        itemKey,
                        { page: args.page, contributionType: args.contributionType }
                    )
        : useAllContributionActivitySubStore().fetchKeyedApiCall(
            { username: args.username, partnerId: args.partnerId },
            itemKey,
            { page: args.page, contributionType: args.contributionType }
        );
}